'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useDemoModal } from '@/components/demo/DemoModalProvider';
import { sendGtmEvent } from '@/lib/gtm/gtm';

// Radix Dialog e il pannello arrivano solo se servono: la maggior parte delle
// visite non vedrà mai l'exit intent.
const ExitIntentDialog = dynamic(() => import('./ExitIntentDialog'), { ssr: false });

/** Una volta per sessione: chi l'ha visto non lo rivede cambiando pagina. */
const STORAGE_KEY = 'tn_exit_intent_shown';
/** Prima di questi secondi sulla pagina non si interviene: uscire subito non è ripensarci. */
const DWELL_MS = 6000;

const wasShown = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const markShown = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Storage disabilitato: si mostrerà di nuovo, pazienza
  }
};

/**
 * Exit intent: quando il mouse esce dalla finestra verso l'alto — la barra
 * degli indirizzi, le schede, il bottone per chiudere — proponiamo la CTA
 * principale del sito e la chat per i dubbi.
 *
 * Solo con un puntatore di precisione (sul telefono non c'è un «fuori»), solo
 * dopo qualche secondo sulla pagina, una volta per sessione, mai sopra a un
 * altro pannello aperto (la demo, l'anteprima delle fonti) e mai su /demo,
 * che il suo modal ce l'ha già.
 */
const ExitIntent = () => {
  const pathname = usePathname();
  const { isOpen: demoOpen } = useDemoModal();
  const demoOpenRef = useRef(demoOpen);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    demoOpenRef.current = demoOpen;
  }, [demoOpen]);

  useEffect(() => {
    if (!pathname || pathname.startsWith('/demo')) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (wasShown()) return;

    const armedAt = Date.now() + DWELL_MS;
    const onLeave = (event: MouseEvent) => {
      // `relatedTarget` nullo = il mouse è uscito dal documento; verso l'alto
      // se la y è sopra lo zero. Di lato o in basso non è un addio.
      if (event.relatedTarget || event.clientY > 0) return;
      if (Date.now() < armedAt) return;
      if (demoOpenRef.current) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      document.removeEventListener('mouseout', onLeave);
      markShown();
      setMounted(true);
      setOpen(true);
      sendGtmEvent({ event: 'exit_intent_shown', page_path: pathname });
    };

    document.addEventListener('mouseout', onLeave);
    return () => document.removeEventListener('mouseout', onLeave);
  }, [pathname]);

  if (!mounted) return null;
  return <ExitIntentDialog open={open} onOpenChange={setOpen} />;
};

export default ExitIntent;
